import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ListingPropertiesService } from './listing-properties.service';

/** Google OAuth status from `GET /google/authurl` (same contract as calendar / email-inbox). */
export interface LeadGoogleAuthStatus {
  connected?: boolean;
  data?: string;
  leadCalendarmanage?: boolean;
}

export interface LeadEmailListPayload {
  inbox: any[];
  sent: any[];
}

export interface LeadEmailListResponse {
  status: boolean;
  data?: LeadEmailListPayload;
}

export interface AiLeadSummaryData {
  summary?: string;
  sentiment?: string;
  source?: string;
  provider?: string;
  model?: string;
  ai_error?: string | null;
  generated_at?: string;
  cached?: boolean;
}

export interface AiLeadSummaryResponse {
  status: boolean;
  message?: string;
  data?: AiLeadSummaryData;
}

interface LeadByIdCacheEntry {
  response$: Observable<any>;
  /** Null while the request is in flight; set once a response arrives and the share window starts. */
  expiryTimer: ReturnType<typeof setTimeout>;
}

function normalizeGoogleAuthResponse(raw: unknown): LeadGoogleAuthStatus {
  if (!raw || typeof raw !== 'object') {
    return { connected: false };
  }
  const envelope = raw as Record<string, unknown>;
  const inner = envelope['data'];
  if (inner && typeof inner === 'object' && 'connected' in (inner as object)) {
    return inner as LeadGoogleAuthStatus;
  }
  return envelope as LeadGoogleAuthStatus;
}

function normalizeLeadEmailListResponse(raw: unknown): LeadEmailListResponse {
  if (!raw || typeof raw !== 'object') {
    return { status: true, data: { inbox: [], sent: [] } };
  }
  const envelope = raw as Record<string, unknown>;

  const toPayload = (payload: Record<string, unknown>): LeadEmailListPayload => ({
    inbox: Array.isArray(payload['inbox']) ? (payload['inbox'] as any[]) : [],
    sent: Array.isArray(payload['sent']) ? (payload['sent'] as any[]) : [],
  });

  if (Array.isArray(envelope['inbox']) || Array.isArray(envelope['sent'])) {
    return { status: true, data: toPayload(envelope) };
  }

  if (envelope['data'] && typeof envelope['data'] === 'object') {
    const data = envelope['data'] as Record<string, unknown>;
    if (Array.isArray(data['inbox']) || Array.isArray(data['sent'])) {
      return {
        status: typeof envelope['status'] === 'boolean' ? (envelope['status'] as boolean) : true,
        data: toPayload(data),
      };
    }
    if (data['data'] && typeof data['data'] === 'object') {
      const inner = data['data'] as Record<string, unknown>;
      if (Array.isArray(inner['inbox']) || Array.isArray(inner['sent'])) {
        return {
          status: typeof data['status'] === 'boolean' ? (data['status'] as boolean) : true,
          data: toPayload(inner),
        };
      }
    }
  }

  return { status: true, data: { inbox: [], sent: [] } };
}

@Injectable({
  providedIn: 'root',
})
export class LeadsService {
  public baseUrl = environment.nodeApiUrl + '/' + environment.api_prefix;
  private readonly LEAD_BY_ID_SHARE_MS = 3000;
  private readonly leadByIdCache = new Map<string, LeadByIdCacheEntry>();

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private _listingPropertiesService: ListingPropertiesService
  ) {}

  getAllLeads(payload: any) {
    return this.api.get(`/get-all-leads?office_id=${this.api.getOfficeId()}`, payload);
  }

  getAllNewLeads(payload: any) {
    return this.api.postCancel(`/get-all-leads-new`, payload);
  }

  getAllProjectLeads(payload: any) {
    return this.api.post(`/get-all-leads-new`, payload);
  }

  /** Takes the same body as `/get-all-leads-new`; counts ignore `not_interested` so options don't zero out on select. */
  getNotInterestedReasonCounts(payload: any) {
    return this.api.post(`/leads/not-interested-reason-counts`, payload);
  }

  /**
   * Whoever opens a lead fetches it, then the lead page fetches it again on mount (and on every tab
   * switch, since the page is rebuilt). The response is reused for a few seconds after it arrives;
   * each subscriber gets its own copy so one caller mutating it can't leak into another. Errors and
   * empty results are never kept, and every lead write below drops the entry.
   */
  getLeadById(id: any): Observable<any> {
    const key = String(id);
    let entry = this.leadByIdCache.get(key);
    if (!entry) {
      const newEntry = { response$: null as Observable<any>, expiryTimer: null as ReturnType<typeof setTimeout> };
      newEntry.response$ = this.api.get(`/lead-detail-by-id/${id}`).pipe(
        tap({
          next: () => {
            clearTimeout(newEntry.expiryTimer);
            newEntry.expiryTimer = setTimeout(() => this.dropLeadByIdEntry(key, newEntry), this.LEAD_BY_ID_SHARE_MS);
          },
          error: () => this.dropLeadByIdEntry(key, newEntry),
          complete: () => {
            if (!newEntry.expiryTimer) {
              this.dropLeadByIdEntry(key, newEntry);
            }
          },
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.leadByIdCache.set(key, newEntry);
      entry = newEntry;
    }
    return entry.response$.pipe(map(response => structuredClone(response)));
  }

  /** Drops the shared getLeadById response for one lead, or for every lead when no id is given. */
  clearLeadByIdCache(id?: any): void {
    if (id === null || id === undefined) {
      this.leadByIdCache.forEach(entry => clearTimeout(entry.expiryTimer));
      this.leadByIdCache.clear();
      return;
    }
    const key = String(id);
    const entry = this.leadByIdCache.get(key);
    if (entry) {
      this.dropLeadByIdEntry(key, entry);
    }
  }

  private dropLeadByIdEntry(key: string, entry: LeadByIdCacheEntry): void {
    clearTimeout(entry.expiryTimer);
    if (this.leadByIdCache.get(key) === entry) {
      this.leadByIdCache.delete(key);
    }
  }

  /**
   * Clears the shared lead both when the write is sent and when it answers — the latter in `tap`, so it
   * runs before the caller's own `next` handler, which often re-fetches the lead straight away.
   */
  private invalidatingLead<T>(request$: Observable<T>, id?: any): Observable<T> {
    this.clearLeadByIdCache(id);
    return request$.pipe(
      tap({
        next: () => this.clearLeadByIdCache(id),
        error: () => this.clearLeadByIdCache(id),
      })
    );
  }

  // Re-queue a project lead's push to Dynamics CRM after a failed sync.
  resyncLeadToDynamic(lead_id: any) {
    return this.invalidatingLead(this.api.get(`/resync-lead-to-dynamic/${lead_id}`), lead_id);
  }

  getAiLeadSummary(leadId: string, payload: Record<string, unknown> = {}) {
    return this.api.post(`/ai-lead-summary/${leadId}`, payload);
  }

  duplicateLeads(payload: any) {
    return this.invalidatingLead(this.api.put(`/eng/misc/entity/duplicate-bulk-leads`, payload));
  }

  // Bulk-edit selected leads. Dedicated lead endpoint — runs the same per-lead save path as a
  // single lead edit (business rules, Dynamics, grid columns, history), once per selected lead.
  // payload: { record: <changed fields>, office_id, selected_records: string[] } — max 250 ids/call.
  bulkEditLead(payload: any) {
    return this.invalidatingLead(this.api.put('/leads/bulk-edit', payload));
  }

  duplicateLead(id: any, payload: any) {
    return this.invalidatingLead(this.api.post(`/leads/duplicate/${id}`, payload), id);
  }
  createLeadDuplication(leadId: string, payload: any) {
    return this.invalidatingLead(this.api.post(`/leads/duplicate/${leadId}`, payload), leadId);
  }
  getProperties(): Observable<any[]> {
    const officeId = this.api.getOfficeId();
    if (!officeId) return new Observable(observer => observer.next([])); // Return empty if no officeId

    return this._listingPropertiesService.getShortListing(false, false, true).pipe(
      map(res =>
        res.data
          .map(item => ({
            ...item,
            property_address: item.property_address || '-',
          }))
          .sort((a, b) => a.property_address.localeCompare(b.property_address))
      )
    );
  }

  getAgents(): Observable<any[]> {
    const officeId = this.api.getOfficeId();
    if (!officeId) return new Observable(observer => observer.next([])); // Return empty if no officeId

    return this.http.get<{ data: any[] }>(this.baseUrl + `/users/byoffice?office=${officeId}&show=staff`).pipe(
      map(res =>
        res.data.map(agent => ({
          ...agent,
          full_name: `${agent.first_name} ${agent.last_name}`,
        }))
      )
    );
  }

  getProjectLeadAgents(project_id: any = ''): Observable<any[]> {
    const officeId = this.api.getOfficeId();
    if (!officeId) return new Observable(observer => observer.next([])); // Return empty if no officeId

    return this.http.get<{ data: any[] }>(this.baseUrl + `/users/lead-agent-responsibles?project=${project_id}`).pipe(
      map(res =>
        res.data.map(agent => ({
          ...agent,
          full_name: `${agent.first_name} ${agent.last_name}`,
        }))
      )
    );
  }

  getLeadStatus(): Observable<any[]> {
    const officeId = this.api.getOfficeId();
    if (!officeId) return new Observable(observer => observer.next([])); // Return empty if no officeId

    return this.http
      .get<{
        data: any[];
      }>(this.baseUrl + `/leads-status-with-lead-count/${officeId}/${this.api.getUserId()}`)
      .pipe(
        map(res =>
          res.data.map(agent => ({
            ...agent,
            full_name: `${agent.first_name} ${agent.last_name}`,
          }))
        )
      );
  }

  json = (item: any[]) => {
    return item?.length > 0 ? JSON.stringify(item) : null;
  };

  getAssignment = (id: string, type?: string) => {
    return this.invalidatingLead(this.api.get(`/leads/remove-assignment/${id}${type ? `?type=${type}` : ''}`), id);
  };

  getLeadSamePersonPrompt = (id: any) => {
    return this.api.get('/leads/same_person_prompt/' + id);
  };

  convertLeadToContact = (id: any, payload: any) => {
    return this.invalidatingLead(this.api.post(`/leads/convert-to-contact/${id}`, payload), id);
  };

  updateLead = (id: any, payload: any) => {
    return this.invalidatingLead(this.api.put(`/update-lead/${id}`, payload), id);
  };

  /**
   * Persist the audit trail produced by the Duplicate Lead Review merge.
   * Backend endpoint pending (see duplicate-lead-merge/README) — callers should
   * treat a failure here as non-fatal to the merge itself.
   */
  saveDuplicateMergeAudit = (payload: any) => {
    return this.api.post('/leads/duplicate-merge-audit', payload);
  };

  getTemplateById = (id: any) => {
    return this.api.get(`/leads-parser/templates-by-id/${id}`);
  };

  updateOfiLikeDislikeStatus = (id: any, payload: any) => {
    return this.invalidatingLead(this.api.put(`/update-lead/${id}`, payload), id);
  };

  /** Builds `/google/authurl` query — backend only supports GET (not POST). */
  private buildGoogleAuthUrlPath(leadId: string, address: string | null = null, email: string | null = null): string {
    const params = new URLSearchParams();
    params.set('leadid', String(leadId));
    params.set('user_id', String(this.api.getUserId()));
    if (address != null && String(address).trim()) {
      params.set('address', String(address).trim());
    }
    if (email != null && String(email).trim()) {
      params.set('femail', String(email).trim());
    }
    return `/google/authurl?${params.toString()}`;
  }

  /**
   * Google connection check for a lead — GET only (same as calendar / email-inbox).
   */
  fetchLeadGoogleAuthStatus = (id: string): Observable<LeadGoogleAuthStatus> => {
    return this.api.get(this.buildGoogleAuthUrlPath(id)).pipe(map(res => normalizeGoogleAuthResponse(res)));
  };

  /**
   * Lead email auth status or inbox/sent list — always GET on `/google/authurl`.
   */
  fetchEmailList(id: any, address?: null, email?: null): Observable<LeadGoogleAuthStatus>;
  fetchEmailList(id: any, address: any, email?: any): Observable<LeadEmailListResponse>;
  fetchEmailList(id: any, address: any = null, email: any = null): Observable<LeadGoogleAuthStatus | LeadEmailListResponse> {
    const url = this.buildGoogleAuthUrlPath(String(id), address, email);
    if (!address && !email) {
      return this.api.get(url).pipe(map(res => normalizeGoogleAuthResponse(res)));
    }
    return this.api.get(url).pipe(map(res => normalizeLeadEmailListResponse(res)));
  }

  getLeadShortInfo = (email: any) => {
    return this.api.get(`/leads-short-detail?office_id=${this.api.getOfficeId()}&email=${encodeURIComponent(email ?? '')}`);
  };

  assignEmailToLead = (payload: any) => {
    return this.invalidatingLead(this.api.post('/new-assign-email-to-lead', payload));
  };

  unassignEmailToLead = (payload: any) => {
    return this.invalidatingLead(this.api.post('/new-unassign-email-to-lead', payload));
  };

  getUnassignLead = () => {
    return this.api.get('/leads/get-unassigned-leads?office_id=' + this.api.getOfficeId());
  };

  getFollowUpView = () => {
    return this.api.get(`/users/office-follow-up-leads-preference/${this.api.getUserId()}/${this.api.getOfficeId()}`);
  };

  saveFollowUpView = (payload: any) => {
    return this.api.post('/users/office-follow-up-leads-preference', payload);
  };

  markEmailPrivate = (payload: any) => {
    return this.api.post('/mark-email-private', payload);
  };

  removeEmailFromLeads = (payload: any) => {
    return this.invalidatingLead(this.api.post('/remove-assigned-email-from-lead', payload));
  };

  sendEmail = (payload: any) => {
    return this.api.post('/email/send', payload);
  };

  getProjectLeadStatus = (_id: any, email: any) => {
    return this.api.get(`/project-leads-exist/${_id}/${email}`);
  };

  checkGoogleStatus = (payload: any) => {
    return this.api.post('/google/check-connected', payload);
  };

  getProjectEvent = (pid: any, lid: any) => {
    return this.api.get(`/calendar/fetch-project-events?project_id=${pid}&lead_id=${lid}`);
  };

  getCollapseFormSettings = (id: any) => {
    return this.api.get('/eng/getall?office_id=' + this.api.getOfficeId() + '&filter=leads' + `&lead_id=${id}` + `&leadformsetting`);
  };

  getSuggestionEmail = (payload: any) => {
    return this.api.get('/email/suggestion', payload);
  };

  assignLead = (payload: any) => {
    return this.invalidatingLead(this.api.post('/leads/assign-lead', payload));
  };

  addNewLead = (payload: any) => {
    return this.api.post('/add-new-lead', payload);
  };

  getOfiCalender = () => {
    return this.api.get(`/calendar/fetch-ofi-and-appt?office_id=${this.api.getOfficeId()}`);
  };

  addInspection = (payload: any) => {
    return this.api.post(`/calendar/insert-property-event`, payload);
  };

  addCommission = (payload: any) => {
    return this.invalidatingLead(this.api.post(`/leads/contact-attempted`, payload));
  };

  cancelAppointment = (payload: any) => {
    return this.api.post('/calendar/cancel-project-appt', payload);
  };

  attendedAppointment = (payload: any) => {
    return this.api.post('/calendar/attendee-project-appt', payload);
  };

  saveAttendeeLike = (payload: any) => {
    return this.api.post('/project-appt-info', payload);
  };

  getProjectOfiLeads = (projectid: any, ofi_id: any) => {
    return this.api.get(`/ofi/project/leads/${projectid}?ofi_id=${ofi_id}&office_id=${this.api.getOfficeId()}`);
  };

  projectAppointmentLikeDislike = (payload: any) => {
    return this.api.post(`/project-appt-feedback`, payload);
  };

  projectContactDetails = (contactId: any, lead_id: any) => {
    return this.api.get(`/ofi/project/contact-related-projects/${contactId}?office_id=${this.api.getOfficeId()}&lead_id=${lead_id}`);
  };

  getLeadsProjectOverview = (extra: any, userIdOverride?: string) => {
    const userId = userIdOverride || this.api.getUserId();
    return this.api.get('/leads/overview-new-projects-v1/' + this.api.getOfficeId() + '/' + userId + extra);
  };

  getLeadsFollowUp = (userIdOverride?: string) => {
    const userId = userIdOverride || this.api.getUserId();
    return this.api.get('/leads/overview-new-follow-up/' + this.api.getOfficeId() + '/' + userId);
  };

  getLeadsListingOverview = (extra: any, userIdOverride?: string) => {
    const officeId = this.api.getOfficeId();
    const userId = userIdOverride || this.api.getUserId();
    return this.api.get(`/leads/overview-new-v1/${officeId}/${userId}${extra}`);
  };

  getAlertSpamParser = payload => {
    const officeId = this.api.getOfficeId();
    return this.api.get(`/inbox/${officeId}`, payload);
  };

  getInboxNotProcess = (id: any, payload) => {
    return this.api.get(`/inbox/${id}`, payload);
  };

  getProcessParser = (payload: any) => {
    return this.api.get(`/inbox/${payload?.templateEmail}`, payload);
  };

  getParseDetailById = (id: string) => {
    return this.api.get(`/lead-parser-detail-by-id/${id}`);
  };

  updateAlertTemplate = (id: any, payload: any) => {
    return this.api.put(`/inbox/${id}`, payload);
  };

  deleteAlterTemplate = (msgid: any) => {
    return this.api.get(`/delete-one-inbox/` + msgid);
  };

  deleteMultipleTemplate = (template_ids: any) => {
    return this.api.post(`/delete-multiple-inbox`, { ids: template_ids });
  };

  moveLeadParser = (payload: any) => {
    return this.api.put(`/inbox/bulk-update`, payload);
  };

  addProjectLeadVolume = (payload: any) => {
    return this.api.post(`/project-lead-volume`, payload);
  };
  updateProjectLeadVolume = (id: string, payload: any) => {
    return this.api.put(`/project-lead-volume/${id}`, payload);
  };
  getProjectLeadVolume = (id: string) => {
    return this.api.get(`/project-lead-volume?project_id=${id}`);
  };
  deleteProjectLeadVolume = (id: string) => {
    return this.api.delete(`/project-lead-volume/${id}`);
  };

  getEntity = (office_id: any = this.api.getOfficeId()) => {
    return this.api.get('/eng/getall?office_id=' + office_id + '&filter=leads');
  };

  getAgentProject = (id: any, payload: any) => {
    return this.api.get('/eng/misc/entity/getall?entityid=' + id, payload);
  };

  getMailBoxes = () => {
    return this.api.get('/office/leads-parser/mailboxs/' + this.api.getOfficeId());
  };

  getTemplates = (page: any, per_page: any, searchText: any, search_agent: any, property: any) => {
    return this.api.get(
      `/office/leads-parser/properties/templates/${this.api.getOfficeId()}?page=${page}&per_page=${per_page}&keyword=${searchText}&agent=${search_agent}&property=${property}`
    );
  };

  createTemplates = (payload: any) => {
    return this.api.post(`/office/leads-parser/properties/templates`, payload);
  };

  getProjectTemplate = (page: any, per_page: any, searchText: any, search_agent: any, search_project: any) => {
    return this.api.get(
      `/office/leads-parser/projects/templates/${this.api.getOfficeId()}?page=${page}&per_page=${per_page}&keyword=${searchText}&agent=${search_agent}&project=${search_project}`
    );
  };

  createProjectTemplate = (payload: any) => {
    return this.api.post(`/office/leads-parser/projects/templates`, payload);
  };

  updateProjectTemplate = (id: any, payload: any) => {
    return this.api.put(`/office/leads-parser/projects/templates/${id}`, payload);
  };

  deleteProjectTemplate = (id: any) => {
    return this.api.delete(`/office/leads-parser/projects/templates/${id}`);
  };

  updateTemplates = (id: any, payload: any) => {
    return this.api.put(`/office/leads-parser/properties/templates/${id}`, payload);
  };

  deleteTemplates = (id: any) => {
    return this.api.delete(`/office/leads-parser/properties/templates/${id}`);
  };

  leadParser = (payload: any) => {
    return this.api.post('/office/leads-parser/property/template', payload);
  };

  updateLeadParser = (id: any, payload: any) => {
    return this.api.put(`/office/leads-parser/properties/templates/${id}`, payload);
  };

  updateLeadAgent = (leadId: any, payload: any) => {
    return this.invalidatingLead(this.api.put('/agent/leads/' + leadId, payload), leadId);
  };

  truncateInbox = (emailAddress: any) => {
    return this.api.get(`/truncate-inbox/${emailAddress}`);
  };

  deleteOneInbox = (msgid: any) => {
    return this.api.get(`/delete-one-inbox/${msgid}`);
  };

  getLeadReminder = (id: any) => {
    return this.api.get(`/lead/getreminder/${id}`);
  };

  getLeadEmail = (id: any) => {
    return this.api.get(`/lead/getemail/${id}`);
  };

  deleteLeadEmail = (id: any) => {
    return this.api.delete(`/lead/deleteemail/${id}`);
  };

  getLeadDefaultEmail = (id: any, projectId: any, officeId: any) => {
    return this.api.get('/lead/defaultemail/' + id + '/' + projectId + '/' + officeId);
  };

  addLeadReminder = (payload: any) => {
    return this.api.post('/lead/reminder', payload);
  };

  addLeadEmail = (id: any, payload: any) => {
    return this.api.post('/lead/email/' + id, payload);
  };

  /**
   * "Remmi To Action Today" (formerly Top Prospects) — the leads to act on today, overdue first, each
   * with a note-grounded summary, reason and next action. `filters` mirrors the leads table's own
   * filter fields (TOP_PROSPECTS_FRONTEND.md §3) so the analysis matches what the agent has on screen.
   * The endpoint keeps its original `lead-insights` path.
   */
  getRemmiTopProspects = (projectId: string, filters: Record<string, any> = {}) => {
    return this.api.get(`/projects/${projectId}/remmi-ai/lead-insights`, filters);
  };

  /**
   * Action Today across projects (REM-3537_NO_RESPONSE_AND_ACTION_TODAY_FRONTEND.md §3) — the Leads
   * module's panel. Same filters and response shape as `getRemmiTopProspects`, merged across every
   * project the user can access, plus `project_ids` / `precinct_ids` (CSV) to narrow it. Each card
   * carries `project`, and its Done / Not Relevant calls stay on that project's own endpoints.
   */
  getRemmiActionTodayAcrossProjects = (filters: Record<string, any> = {}) => {
    return this.api.get(`/remmi-ai/lead-insights`, filters);
  };

  /**
   * Whether Remmi suggests closing this lead as No Further Interest (REM-3537_2026-10-13 §4). Show
   * the lead page banner only when `data.suggest` is true, using `title` / `message` as sent.
   */
  getLeadCloseSuggestion = (leadId: string) => {
    return this.api.get(`/remmi-ai/leads/${leadId}/close-suggestion`);
  };

  /** "Keep as Contact Attempted" — hides the suggestion (banner and Action Today) until 3 more unanswered attempts. */
  dismissLeadCloseSuggestion = (leadId: string) => {
    return this.api.post(`/remmi-ai/leads/${leadId}/close-suggestion/dismiss`, {});
  };

  /**
   * Cheap re-check of today's list — counts plus which recommendations have been actioned since the
   * last load. Does NOT re-run the analysis, so it is safe to call whenever the agent closes a lead.
   */
  getRemmiTopProspectsStatus = (projectId: string) => {
    return this.api.get(`/projects/${projectId}/remmi-ai/lead-insights/status`);
  };

  /**
   * "Done" — the recommendation was valid and the agent has dealt with the lead. It moves into
   * "Actioned today" (still visible there) rather than being deleted, and is re-assessed tomorrow.
   */
  markRemmiTopProspectDone = (projectId: string, leadId: string) => {
    return this.api.post(`/projects/${projectId}/remmi-ai/lead-insights/${leadId}/actioned`, {});
  };

  /**
   * "Remmi got this wrong" — Remmi should not have recommended this lead at all. Distinct from Done:
   * it is negative feedback on the recommendation and drops the lead off today's list entirely.
   * `payload` carries the agent's `{ reason, comment }` so the correction is actionable rather than
   * a bare downvote (see project-leads-remmi-ai-wrong-feedback-backend-requirements.md).
   */
  markRemmiTopProspectNotRelevant = (projectId: string, leadId: string, payload: Record<string, any> = {}) => {
    return this.api.post(`/projects/${projectId}/remmi-ai/lead-insights/${leadId}/not-relevant`, payload);
  };

  /**
   * Reverses today's rejection so the lead is a candidate again on the next list fetch. Backs the
   * Undo on the feedback toast — without it a misclick costs the agent that lead for the whole day.
   * The stored feedback is marked withdrawn rather than deleted. 404s when there is nothing to undo.
   */
  undoRemmiTopProspectNotRelevant = (projectId: string, leadId: string) => {
    return this.api.delete(`/projects/${projectId}/remmi-ai/lead-insights/${leadId}/not-relevant`);
  };

  /**
   * The project's Action Today settings: `excluded_statuses` (lead statuses hidden from Action Today)
   * plus `status_groups`, the office's active Lead / Agent Lead statuses to pick from.
   */
  getActionTodaySettings = (projectId: string) => {
    return this.api.get(`/projects/${projectId}/remmi-ai/action-today-settings`);
  };

  /** Replaces the stored selection — always send the full list; `[]` excludes nothing. */
  saveActionTodaySettings = (projectId: string, excludedStatuses: string[]) => {
    return this.api.put(`/projects/${projectId}/remmi-ai/action-today-settings`, { excluded_statuses: excludedStatuses });
  };

  /**
   * What agents have told Remmi it got wrong, newest first, across every project the caller can see.
   * Takes `projectId` (a JSON-stringified array of ids; omitted means all), `keyword`, `status`,
   * `from`/`to` (`YYYY-MM-DD`, the caller's timezone) and `limit`. Withdrawn rejections come back
   * flagged rather than hidden — "taken back" is a different fact from "never happened".
   *
   * Office-wide by design: the Settings page reporting on it is not scoped to one project. See
   * `src/app/modules/setting/remmi-feedback/remmi-feedback-backend-requirements.md`.
   *
   * The query is built here rather than handed to `ApiService`, whose `HttpParams.set()` would
   * collapse the array into a comma-joined value: `projectId` goes out once, carrying the ids as
   * JSON (`projectId=["a","b"]`), the same shape `json()` above sends array filters in.
   */
  getRemmiFeedback = (filters: { projectId?: string[]; keyword?: string; status?: string; from?: string; to?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    const projectId = this.json(filters.projectId || []);
    if (projectId) {
      query.set('projectId', projectId);
    }
    ['keyword', 'status', 'from', 'to', 'limit'].forEach(key => {
      const value = filters[key];
      if (value != null && `${value}`.trim() !== '') {
        query.set(key, `${value}`);
      }
    });
    const queryString = query.toString();
    return this.api.get(`/remmi-ai/lead-insights/feedback${queryString ? `?${queryString}` : ''}`);
  };
}
